import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { INTERACTION_TYPES } from '@cult/domain';
import type { PublicPraise } from '@cult/schemas';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { App as AntApp, Button, Popconfirm, Space, Typography } from 'antd';
import { BodyRender, EditWindowNotice } from '../../components';
import { AttachmentsView } from '../../features/attachments';
import {
  DetailSection,
  INTERACTION_TYPE_META,
  InteractionDetailAside,
  InteractionDetailError,
  InteractionDetailHeader,
  InteractionDetailLayout,
  InteractionDetailSkeleton,
} from '../../features/interactions';
import { useDeletePraise, usePraise } from '../../features/praises';
import { ReferenceTags } from '../../features/reference';
import { useCurrentUser } from '../../state/use-current-user';

const { Title, Text } = Typography;

const AuthorActions = ({ praise }: { praise: PublicPraise }) => {
  const navigate = useNavigate();
  const { message } = AntApp.useApp();
  const deletePraise = useDeletePraise();

  const onDelete = () =>
    deletePraise.mutate(praise, {
      onSuccess: () => {
        message.success('Praise deleted');
        navigate({ to: '/praises' });
      },
      onError: (err) => message.error(`Couldn't delete praise: ${err.message}`),
    });

  return (
    <Space>
      <Button
        size="small"
        icon={<EditOutlined />}
        onClick={() => navigate({ to: '/praises/$praiseId/edit', params: { praiseId: String(praise.number) } })}
      >
        Edit
      </Button>
      <Popconfirm
        title="Delete this praise?"
        description="This also removes its comments, attachments, and awarded points."
        okText="Delete"
        okButtonProps={{ danger: true, loading: deletePraise.isPending }}
        onConfirm={onDelete}
      >
        <Button danger size="small" icon={<DeleteOutlined />}>
          Delete
        </Button>
      </Popconfirm>
    </Space>
  );
};

const PraiseDetailPage = () => {
  const { id } = Route.useParams();
  const praiseQuery = usePraise(id);
  const userId = useCurrentUser((s) => s.user?.id);

  if (praiseQuery.isPending) return <InteractionDetailSkeleton />;
  if (praiseQuery.isError) {
    return (
      <InteractionDetailError
        type={INTERACTION_TYPES.PRAISE}
        title="Couldn't load praise"
        message={praiseQuery.error.message}
      />
    );
  }

  const praise = praiseQuery.data;
  const accent = praise.coreValues[0]?.color ?? INTERACTION_TYPE_META[INTERACTION_TYPES.PRAISE].accent;

  return (
    <InteractionDetailLayout
      type={INTERACTION_TYPES.PRAISE}
      accent={accent}
      notice={
        praise.author.id === userId ? (
          <EditWindowNotice createdAt={praise.createdAt} noun="praise" actions={<AuthorActions praise={praise} />} />
        ) : null
      }
      aside={
        <InteractionDetailAside
          author={praise.author}
          receiver={praise.receiver}
          createdAt={praise.createdAt}
          receiverLabel="Recognized"
        />
      }
    >
      <InteractionDetailHeader
        type={INTERACTION_TYPES.PRAISE}
        author={praise.author}
        receiver={praise.receiver}
        createdAt={praise.createdAt}
      />

      <Title level={2} style={{ margin: '12px 0' }}>
        {praise.title}
      </Title>

      <ReferenceTags tags={praise} style={{ marginBottom: 4 }} />

      <DetailSection title="What happened">
        <BodyRender source={praise.whatHappened} />
      </DetailSection>

      <DetailSection title="Behavior">
        <Text>{praise.behavior}</Text>
      </DetailSection>

      <AttachmentsView attachments={praise.attachments} />
    </InteractionDetailLayout>
  );
};

export const Route = createFileRoute('/_auth/praise/$id')({
  component: PraiseDetailPage,
});
